import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import apiService from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { User } from '../../types';
import websocketService from '../../services/websocketService';
import erlangWebSocketService from '../../services/erlangWebSocketService';
import { decryptMessage } from '../../utils/encryption';
import MessageSkeleton from './MessageSkeleton';
import styles from '../../styles/Chat.module.css';
import skeletonStyles from '../../styles/SkeletonLoader.module.css';
import SkeletonLoader from '../UI/SkeletonLoader';

// Helper function to convert file to base64
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = error => reject(error);
  });
};

interface ChatUser {
  id: string;
  name: string;
  username: string;
  avatar?: string;
}

interface ChatMessage {
  id: string;
  sender: ChatUser;
  content: string;
  message_type: 'text' | 'image' | 'file' | 'system' | 'forwarded';
  status: 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
  created_at: string;
  edited_at?: string;
  reply_to?: {
    id: string;
    content: string;
    sender: string;
  };
  forwarded_from?: {
    id: string;
    content: string;
    sender: string;
  };
  attachments?: Array<{
    id: string;
    filename: string;
    file_size: number;
    file_type: string;
    page_count?: number;
    url: string;
    thumbnail_url?: string;
  }>;
  read_by?: Array<{
    user_id: string;
    user_name: string;
    status: string;
    timestamp: string;
  }>;
}

interface ChatParticipant {
  id: string;
  name: string;
  username: string;
  avatar?: string;
  is_admin: boolean;
  is_muted: boolean;
  joined_at: string;
  last_seen: string;
}

interface ChatRoom {
  id: string;
  name: string;
  description: string;
  created_at: string;
  created_by: {
    id: string;
    name: string;
  };
}

interface ChatData {
  chat_room: ChatRoom;
  participants: ChatParticipant[];
  messages: ChatMessage[];
  user_is_participant: boolean;
}

interface ChatProps {
  groupId?: string;
}

const Chat: React.FC<ChatProps> = ({ groupId: propGroupId }) => {
  const { groupId, taskId } = useParams<{ groupId: string; taskId: string }>();
  const notificationService = useNotification();
  const { user: currentUser } = useAuth();

  // Determine if we're in admin context or member context
  const isAdminContext = window.location.pathname.includes('/admin/groups/');

  // Get the actual groupId and taskId based on context
  const actualGroupId = isAdminContext ? groupId : propGroupId;
  const actualTaskId = taskId;

  // For member context, we need to get groupId from the task data
  const [taskGroupId, setTaskGroupId] = useState<string | null>(propGroupId || null);
  
  const [chatData, setChatData] = useState<ChatData | null>(null);
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [replyingTo, setReplyingTo] = useState<ChatMessage | null>(null);
  const [typingUsers, setTypingUsers] = useState<Set<string>>(new Set());
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 20,
    total_messages: 0,
    total_pages: 0,
    has_next: false,
    has_previous: false
  });
  const [isTyping, setIsTyping] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null);
  const [isUserAtBottom, setIsUserAtBottom] = useState(true);
  const [typingUsersList, setTypingUsersList] = useState<Array<{id: string, name: string, avatar?: string}>>([]);
  const [typingTimeouts, setTypingTimeouts] = useState<Map<string, NodeJS.Timeout>>(new Map());
  
  // Debug typing state
  useEffect(() => {
    console.log('📊 Typing users list updated:', typingUsersList);
    if (typingUsersList.length > 0) {
      console.log('🎭 Should render typing indicator with', typingUsersList.length, 'users');
    }
  }, [typingUsersList]);
  
  // Erlang WebSocket state
  const [useErlangWebSocket, setUseErlangWebSocket] = useState(false);
  const [erlangConnectionStatus, setErlangConnectionStatus] = useState<'disconnected' | 'connecting' | 'connected' | 'failed'>('disconnected');
  const userPrivateKeyRef = useRef<string | null>(null);
  const handleFileAction = (url: string, filename: string, action: 'view' | 'download') => {
    console.log('File action triggered:', { url, filename, action });
    
    if (!url) {
      console.error('File URL is not available');
      notificationService.error('File URL is not available');
      return;
    }

    // Ensure URL is absolute
    let absoluteUrl = url;
    if (!url.startsWith('http')) {
      // If it's a relative URL, make it absolute
      const baseUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';
      absoluteUrl = `${baseUrl}${url.startsWith('/') ? url : `/${url}`}`;
    }

    console.log('Final URL:', absoluteUrl);

    if (action === 'view') {
      try {
      window.open(absoluteUrl, '_blank');
        console.log('Opened file in new tab');
      } catch (error) {
        console.error('Error opening file:', error);
        notificationService.error('Failed to open file');
      }
    } else {
      try {
      // Download
      const link = document.createElement('a');
      link.href = absoluteUrl;
      link.download = filename;
        link.target = '_blank'; // Fallback for browsers that don't support download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
        console.log('Downloaded file:', filename);
      } catch (error) {
        console.error('Error downloading file:', error);
        notificationService.error('Failed to download file');
      }
    }
  };

  // Erlang WebSocket connection with Django fallback
  useEffect(() => {
    const finalGroupId = isAdminContext ? actualGroupId : (propGroupId || taskGroupId);

    if (finalGroupId && actualTaskId && currentUser) {
      // Try Erlang WebSocket first for maximum performance
      connectToErlangWebSocket(finalGroupId, actualTaskId, currentUser.id)
        .then(() => {
          setUseErlangWebSocket(true);
          setErlangConnectionStatus('connected');
        })
        .catch((error) => {
          console.warn('⚠️ Erlang WebSocket failed, falling back to Django WebSocket:', error);
          setErlangConnectionStatus('failed');
          connectToDjangoWebSocket(finalGroupId, actualTaskId);
        });

      return () => {
        // Cleanup both connections
        cleanupConnections();
      };
    }
  }, [isAdminContext, actualGroupId, taskGroupId, actualTaskId, currentUser]);

  // Connect to Erlang WebSocket
  const connectToErlangWebSocket = async (groupId: string, taskId: string, userId: string) => {
    setErlangConnectionStatus('connecting');
    
    try {
      await erlangWebSocketService.connect(`${groupId}_${taskId}`, userId);
      
      // Set up Erlang event listeners
      erlangWebSocketService.on('new_message', handleErlangMessage);
      erlangWebSocketService.on('typing_start', handleErlangTypingStart);
      erlangWebSocketService.on('typing_stop', handleErlangTypingStop);
      erlangWebSocketService.on('connected', handleErlangJoined); // Listen for connected event
      erlangWebSocketService.on('joined', handleErlangJoined);
      erlangWebSocketService.on('left', handleErlangLeft);
      erlangWebSocketService.on('messages', handleErlangMessages);
      erlangWebSocketService.on('users', handleErlangUsers);
      erlangWebSocketService.on('message_status_update', handleErlangMessageStatusUpdate);
      
      // Join the room with user name
      const userName = currentUser?.first_name && currentUser?.last_name 
        ? `${currentUser.first_name} ${currentUser.last_name}`.trim()
        : currentUser?.first_name || currentUser?.last_name || currentUser?.username || `User ${userId}`;
      
      console.log('🔑 Joining room with user name:', userName, 'for user:', userId);
      erlangWebSocketService.joinRoom(userId, userName);
      
      // Load existing messages after a short delay to ensure connection is stable
      setTimeout(() => {
        console.log('🔄 Requesting messages from WebSocket...');
        erlangWebSocketService.getMessages();
      }, 1000);
      
      // Get online users
      erlangWebSocketService.getUsers();
      
    } catch (error) {
      console.error('Erlang WebSocket connection failed:', error);
      throw error;
    }
  };

  // Connect to Django WebSocket (fallback)
  const connectToDjangoWebSocket = (groupId: string, taskId: string) => {
    setUseErlangWebSocket(false);
    
    // Connect to Django WebSocket
    websocketService.connect(groupId, taskId, 'dummy-token');

    // Set up Django event listeners
    websocketService.on('chat_message', handleWebSocketMessage);
    websocketService.on('typing_start', handleTypingStart);
    websocketService.on('typing_stop', handleTypingStop);
    websocketService.on('message_read_update', handleMessageReadUpdate);
    websocketService.on('connection_failed', handleConnectionFailed);
  };

  // Cleanup connections
  const cleanupConnections = () => {
    // Clear typing indicators and timeout
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }
    
    if (useErlangWebSocket) {
      // Cleanup Erlang WebSocket
      erlangWebSocketService.off('new_message', handleErlangMessage);
      erlangWebSocketService.off('typing_start', handleErlangTypingStart);
      erlangWebSocketService.off('typing_stop', handleErlangTypingStop);
      erlangWebSocketService.off('connected', handleErlangJoined); // Remove connected event listener
      erlangWebSocketService.off('joined', handleErlangJoined);
      erlangWebSocketService.off('left', handleErlangLeft);
      erlangWebSocketService.off('messages', handleErlangMessages);
      erlangWebSocketService.off('users', handleErlangUsers);
      erlangWebSocketService.off('message_status_update', handleErlangMessageStatusUpdate);
      
      erlangWebSocketService.leaveRoom(currentUser?.id || '');
      erlangWebSocketService.disconnect();
    } else {
      // Cleanup Django WebSocket
      websocketService.off('chat_message', handleWebSocketMessage);
      websocketService.off('typing_start', handleTypingStart);
      websocketService.off('typing_stop', handleTypingStop);
      websocketService.off('message_read_update', handleMessageReadUpdate);
      websocketService.off('connection_failed', handleConnectionFailed);
      
      websocketService.sendTypingStop();
      websocketService.disconnect();
    }
    
    setIsTyping(false);
    setTypingUsers(new Set());
    setTypingUsersList([]);
    
    // Clear all typing timeouts
    typingTimeouts.forEach(timeout => clearTimeout(timeout));
    typingTimeouts.clear();
  };

  // Erlang WebSocket event handlers
  const handleErlangMessage = useCallback(async (data: any) => {
    console.log('📨 Received message event:', data);
    
    if (data.message) {
      let decryptedContent = data.message.content;
      
      console.log('🔍 Message content length:', data.message.content.length);
      console.log('🔑 User private key available:', !!userPrivateKeyRef.current);
      
      // Decrypt message if we have a private key and content looks encrypted
      if (userPrivateKeyRef.current && data.message.content && data.message.content.length > 100) {
        try {
          console.log('🔓 Attempting to decrypt message...');
          decryptedContent = await decryptMessage(data.message.content, userPrivateKeyRef.current);
          console.log('🔓 Decrypted message:', decryptedContent);
        } catch (error) {
          console.error('❌ Failed to decrypt message:', error);
          decryptedContent = data.message.content; // Fallback to original
        }
      } else {
        console.log('⚠️ Skipping decryption - no private key or content too short');
      }
      
      const message: ChatMessage = {
        id: data.message.id,
        sender: {
          id: data.message.user_id,
          name: data.message.user_name || 'Unknown User',
          username: data.message.user_id,
          avatar: data.message.user_avatar
        },
        content: decryptedContent,
        message_type: data.message.message_type || 'text',
        status: data.message.status || 'delivered', // Use status from backend
        created_at: new Date(data.message.timestamp).toISOString(),
        attachments: data.message.attachments || []
      };
      
      setChatData(prev => {
        if (!prev) return null;
        
        // Check if message already exists to prevent duplicates
        const messageExists = prev.messages.some(msg => 
          msg.id === message.id && msg.created_at === message.created_at
        );
        if (messageExists) {
          return prev;
        }
        
        // Check if this is a replacement for a temporary message
        const tempMessageIndex = prev.messages.findIndex(msg => 
          msg.id.startsWith('temp-') && 
          msg.sender.id === message.sender.id && 
          msg.content === message.content &&
          Math.abs(new Date(msg.created_at).getTime() - new Date(message.created_at).getTime()) < 5000 // Within 5 seconds
        );
        
        if (tempMessageIndex !== -1) {
          // Replace the temporary message with the real one
          const updatedMessages = [...prev.messages];
          updatedMessages[tempMessageIndex] = message;
          
          return {
            ...prev,
            messages: updatedMessages
          };
        }
        
        // Add new message if no temporary message to replace
        return {
          ...prev,
          messages: [...prev.messages, message]
        };
      });

      // Smart scroll - only scroll if user is at bottom
      if (isUserAtBottom) {
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 10);
      }
    }
  }, [isUserAtBottom]);

  const handleErlangTypingStart = useCallback((data: any) => {
    console.log('🎯 Received typing_start:', data);
    console.log('🔍 User name received:', data.user_name, 'Length:', data.user_name?.length);
    
    if (data.user_id !== currentUser?.id) {
      setTypingUsersList(prev => {
        const userExists = prev.some(user => user.id === data.user_id);
        if (userExists) {
          console.log('👤 User already typing, extending timeout');
          return prev; // User already in list, just extend timeout
        }
        
        const newUser = {
          id: data.user_id,
          name: data.user_name && data.user_name.length < 50 && !data.user_name.includes('-----BEGIN') 
            ? data.user_name 
            : 'Someone',
          avatar: data.user_avatar || null
        };
        
        console.log('➕ Adding new typing user:', newUser);
        return [...prev, newUser];
      });
      
      // Clear any existing timeout for this user
      const existingTimeout = typingTimeouts.get(data.user_id);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }
      
      // Set new timeout to auto-clear after 2 seconds
      const timeout = setTimeout(() => {
        console.log('⏰ Auto-clearing typing for user:', data.user_id);
        setTypingUsersList(prev => prev.filter(user => user.id !== data.user_id));
        typingTimeouts.delete(data.user_id);
      }, 2000);
      
      typingTimeouts.set(data.user_id, timeout);
    }
  }, [currentUser]);

  const handleErlangTypingStop = useCallback((data: any) => {
    console.log('🛑 Received typing_stop:', data);
    
    if (data.user_id !== currentUser?.id) {
      // Clear timeout for this user
      const existingTimeout = typingTimeouts.get(data.user_id);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
        typingTimeouts.delete(data.user_id);
      }
      
      // Remove user from typing list immediately
      setTypingUsersList(prev => {
        const filtered = prev.filter(user => user.id !== data.user_id);
        console.log('➖ Removed typing user:', data.user_id, 'Remaining:', filtered.length);
        return filtered;
      });
    }
  }, [currentUser, typingTimeouts]);

  const handleErlangJoined = useCallback((data: any) => {
    console.log('🔑 Received event with data:', data);
    
    // Store the private key for decryption
    if (data.private_key) {
      userPrivateKeyRef.current = data.private_key;
      console.log('🔑 Private key received and stored:', data.private_key.substring(0, 50) + '...');
    } else {
      console.log('❌ No private key in event data');
    }
  }, []);

  const handleErlangLeft = useCallback((data: any) => {
    // User left - could update online users list here
  }, []);

  const handleErlangMessages = useCallback(async (data: any) => {
    console.log('📋 Received messages event:', data);
    console.log('🔑 User private key available:', !!userPrivateKeyRef.current);
    console.log('📊 Messages count:', data.messages?.length || 0);
    console.log('📊 Pagination:', data.pagination);
    
    // Update pagination state
    if (data.pagination) {
      setPagination(data.pagination);
    }
    
    if (data.messages && data.messages.length > 0) {
      console.log('📨 Processing', data.messages.length, 'messages');
      
      const messages: ChatMessage[] = await Promise.all(
        data.messages.map(async (msg: any) => {
          let decryptedContent = msg.content;
          
          console.log('🔍 Message content length:', msg.content.length);
          
          // Decrypt message if we have a private key and content looks encrypted
          if (userPrivateKeyRef.current && msg.content && msg.content.length > 100) {
            try {
              console.log('🔓 Attempting to decrypt message...');
              decryptedContent = await decryptMessage(msg.content, userPrivateKeyRef.current);
              console.log('🔓 Decrypted message:', decryptedContent);
            } catch (error) {
              console.error('❌ Failed to decrypt message:', error);
              decryptedContent = msg.content; // Fallback to original
            }
          } else {
            console.log('⚠️ Skipping decryption - no private key or content too short');
          }
          
          // Debug attachments
          console.log(`📎 Processing message ${msg.id}:`, {
            message_type: msg.message_type,
            attachments_count: msg.attachments ? msg.attachments.length : 0,
            attachments: msg.attachments
          });

          return {
            id: msg.id,
            sender: {
              id: msg.user_id,
              name: msg.user_name || 'Unknown User',
              username: msg.user_id,
              avatar: msg.user_avatar
            },
            content: decryptedContent,
            message_type: msg.message_type || 'text',
            status: msg.status || 'sent', // Use group-wide status from backend
            created_at: new Date(msg.timestamp).toISOString(),
            attachments: msg.attachments || []
          };
        })
      );
      
      // Initialize chat data if it doesn't exist
      setChatData(prev => {
        const chatData = prev || {
          chat_room: {
            id: 'temp-room',
            name: 'Chat Room',
            description: '',
            created_at: new Date().toISOString(),
            created_by: { id: '', name: '' }
          },
          participants: [],
          messages: [],
          user_is_participant: true
        };
        
        return {
          ...chatData,
          messages: messages.reverse() // Show oldest first
        };
      });
      
      // Stop loading
      setLoading(false);
      setIsLoadingMore(false);
    } else {
      // No messages, but initialize empty chat data
      setChatData(prev => {
        const chatData = prev || {
          chat_room: {
            id: 'temp-room',
            name: 'Chat Room',
            description: '',
            created_at: new Date().toISOString(),
            created_by: { id: '', name: '' }
          },
          participants: [],
          messages: [],
          user_is_participant: true
        };
        
        return chatData;
      });
      
      // Stop loading
      setLoading(false);
      setIsLoadingMore(false);
    }
  }, []);

  const loadMoreMessages = useCallback(() => {
    if (isLoadingMore || !pagination.has_next) return;
    
    setIsLoadingMore(true);
    console.log('📤 Requesting more messages...');
    erlangWebSocketService.send({
      type: 'get_messages',
      page: pagination.current_page + 1,
      page_size: pagination.page_size
    });
  }, [isLoadingMore, pagination]);

  const handleErlangUsers = useCallback((data: any) => {
    // Online users updated - could update participants list here
  }, []);

  const handleErlangMessageStatusUpdate = useCallback((data: any) => {
    console.log('📊 Received message status update:', data);
    
    // Update message status
    setChatData(prev => {
      if (!prev) return null;
      
      return {
        ...prev,
        messages: prev.messages.map(message => {
          if (message.id === data.message_id) {
            // Update the read_by array for read status
            if (data.status === 'read') {
              const updatedReadBy = [...(message.read_by || [])];
              const existingReadIndex = updatedReadBy.findIndex(read => read.user_id === data.user_id);
              
              if (existingReadIndex >= 0) {
                // Update existing read status
                updatedReadBy[existingReadIndex] = {
                  user_id: data.user_id,
                  user_name: data.user_name || 'Unknown User',
                  status: 'read',
                  timestamp: data.timestamp
                };
              } else {
                // Add new read status
                updatedReadBy.push({
                  user_id: data.user_id,
                  user_name: data.user_name || 'Unknown User',
                  status: 'read',
                  timestamp: data.timestamp
                });
              }
              
              // Determine overall message status
              let messageStatus = message.status;
              if (updatedReadBy.length > 0) {
                // Check if all participants have read the message
                const allParticipantsRead = prev.participants.every(participant => 
                  participant.id === message.sender.id || // Don't check sender
                  updatedReadBy.some(read => read.user_id === participant.id)
                );
                
                if (allParticipantsRead) {
                  messageStatus = 'read';
                } else {
                  messageStatus = 'delivered';
                }
              }
              
              return {
                ...message,
                status: messageStatus,
                read_by: updatedReadBy
              };
            }
            
            // For delivered status, update the overall message status
            if (data.status === 'delivered') {
              return {
                ...message,
                status: 'delivered'
              };
            }
            
            return message;
          }
          return message;
        })
      };
    });
  }, []);

  // Django WebSocket event handlers (fallback)
  const handleWebSocketMessage = useCallback((data: any) => {
    console.log('🔄 Received Django WebSocket message:', data);

    if (data.message) {
      setChatData(prev => {
        if (!prev) return null;
        
        // Check if message already exists to prevent duplicates
        const messageExists = prev.messages.some(msg => 
          msg.id === data.message.id && msg.created_at === data.message.created_at
        );
        if (messageExists) {
          console.log('Message already exists, skipping duplicate:', data.message.id);
          return prev;
        }
        
        return {
          ...prev,
          messages: [...prev.messages, data.message]
        };
      });

      // Smart scroll - only scroll if user is at bottom
      if (isUserAtBottom) {
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 50);
      }
    }
  }, [isUserAtBottom]);

  const handleTypingStart = useCallback((data: any) => {
    console.log('Received typing start data:', data);
    
    if (data.user_id !== currentUser?.id) {
      // Add user to typing list with avatar and name
      setTypingUsersList(prev => {
        const userExists = prev.some(user => user.id === data.user_id);
        if (userExists) {
          return prev; // User already in list
        }
        
        // Add new typing user with proper data
        const newUser = {
          id: data.user_id,
          name: data.user_full_name || data.user_name || 'Someone',
          avatar: data.user_avatar || null
        };
        
        console.log('Adding typing user:', newUser);
        return [...prev, newUser];
      });
      
      // Auto-clear typing indicator after 1.5 seconds (faster than before)
      setTimeout(() => {
        setTypingUsersList(prev => prev.filter(user => user.id !== data.user_id));
      }, 1500);
    }
  }, [currentUser]);

  const handleTypingStop = useCallback((data: any) => {
    if (data.user_id !== currentUser?.id) {
      // Remove user from typing list immediately
      setTypingUsersList(prev => prev.filter(user => user.id !== data.user_id));
    }
  }, [currentUser]);

  const handleMessageReadUpdate = useCallback((data: any) => {
    console.log('Received message read update:', data);
    
    // Update the message status in the chat data
    setChatData(prev => {
      if (!prev) return null;
      
      return {
        ...prev,
        messages: prev.messages.map(message => {
          if (message.id === data.message_id) {
            // Update the read_by array
            const updatedReadBy = [...(message.read_by || [])];
            const existingReadIndex = updatedReadBy.findIndex(read => read.user_id === data.user_id);
            
            if (existingReadIndex >= 0) {
              // Update existing read status
              updatedReadBy[existingReadIndex] = {
                user_id: data.user_id,
                user_name: data.user_name,
                status: 'read',
                timestamp: data.timestamp
              };
            } else {
              // Add new read status
              updatedReadBy.push({
                user_id: data.user_id,
                user_name: data.user_name,
                status: 'read',
                timestamp: data.timestamp
              });
            }
            
            // Determine overall message status
            let messageStatus = message.status;
            if (updatedReadBy.length > 0) {
              // Check if all participants have read the message
              const allParticipantsRead = prev.participants.every(participant => 
                participant.id === message.sender.id || // Don't check sender
                updatedReadBy.some(read => read.user_id === participant.id)
              );
              
              if (allParticipantsRead) {
                messageStatus = 'read';
              } else {
                messageStatus = 'delivered';
              }
            }
            
            return {
              ...message,
              status: messageStatus,
              read_by: updatedReadBy
            };
          }
          return message;
        })
      };
    });
  }, []);

  const handleConnectionFailed = useCallback((data: any) => {
    console.error('WebSocket connection failed:', data);
    notificationService.error('Failed to connect to chat server');
  }, [notificationService]);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);

  // Load task details to get groupId for member context (only if no groupId prop provided)
  const loadTaskDetails = useCallback(async () => {
    if (!actualTaskId || isAdminContext || propGroupId) return;

    try {
      console.log('Loading task details for member context:', { taskId: actualTaskId });
      const response = await apiService.getTaskDetails(actualTaskId);
      console.log('Task details response:', response);

      if (response.status === 'success' && response.data.group) {
        setTaskGroupId(response.data.group.id);
        console.log('Set taskGroupId:', response.data.group.id);
      } else {
        console.error('Failed to get task details:', response.message);
        notificationService.error('Failed to load task details');
      }
    } catch (error: any) {
      console.error('Error loading task details:', error);
      notificationService.error('Failed to load task details');
    }
  }, [actualTaskId, isAdminContext, propGroupId, notificationService]);

  // Load task details when in member context (only if no groupId prop provided)
  useEffect(() => {
    if (!isAdminContext && actualTaskId && !propGroupId) {
      loadTaskDetails();
    }
  }, [loadTaskDetails, isAdminContext, actualTaskId, propGroupId]);

  // Load chat data
  // Load messages from WebSocket (no Django API calls needed)
  const loadMessagesFromWebSocket = useCallback(() => {
    console.log('🔄 loadMessagesFromWebSocket called');
    console.log('🔌 useErlangWebSocket:', useErlangWebSocket);
    console.log('🔗 WebSocket connected:', erlangWebSocketService.isConnected());
    
    if (useErlangWebSocket && erlangWebSocketService.isConnected()) {
      console.log('📤 Sending get_messages request...');
      // Request messages from WebSocket
      erlangWebSocketService.send({
        type: 'get_messages'
      });
    } else {
      console.log('⚠️ Cannot request messages - WebSocket not ready');
    }
  }, [useErlangWebSocket]);

  useEffect(() => {
    loadMessagesFromWebSocket();
  }, [loadMessagesFromWebSocket]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [chatData?.messages]);

  // Mark messages as read when window gains focus or messages become visible
  useEffect(() => {
    const handleWindowFocus = () => {
      // Removed automatic message marking as read
      // Messages should only be marked as read when user actually reads them
    };

    const handleWindowBlur = () => {
      // Optional: Handle window blur if needed
    };

    // Add event listeners
    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('blur', handleWindowBlur);

    // Mark messages as read immediately when component mounts (initial focus)
    handleWindowFocus();

    // Cleanup
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [chatData?.messages, currentUser]);

  // Mark messages as read when they become visible (Intersection Observer)
  useEffect(() => {
    // Removed automatic message marking as read
    // Messages should only be marked as read when user actually reads them
  }, [chatData?.messages, currentUser]);

  // Smart scroll logic - only scroll if user is at bottom
  const scrollToBottom = useCallback(() => {
    if (isUserAtBottom) {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isUserAtBottom]);

  // Check if user is at bottom of messages
  const checkIfAtBottom = useCallback(() => {
    const messagesContainer = messagesEndRef.current?.parentElement;
    if (messagesContainer) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainer;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 50; // 50px threshold
      setIsUserAtBottom(isAtBottom);
    }
  }, []);

  // Handle scroll events to track user position
  const handleScroll = useCallback(() => {
    checkIfAtBottom();
  }, [checkIfAtBottom]);

  // Send message - Pure Erlang WebSocket (no Django API calls)
  const sendMessage = async (content: string, messageType: 'text' | 'file' = 'text', attachments?: File[]) => {
    if (!content.trim() && messageType === 'text') return;

    const finalGroupId = isAdminContext ? actualGroupId : (propGroupId || taskGroupId);
    if (!finalGroupId || !actualTaskId || !currentUser) return;

    setSendingMessage(true);
    try {
      if (useErlangWebSocket) {
        // Process attachments to convert files to base64
        const processedAttachments = [];
        if (attachments && attachments.length > 0) {
          for (const file of attachments) {
            try {
              const base64 = await fileToBase64(file);
              processedAttachments.push({
                filename: file.name,
                file_size: file.size,
                file_type: file.type,
                data: base64 // Send actual file data as base64
              });
            } catch (error) {
              console.error('Error converting file to base64:', error);
              // Fallback to blob URL if base64 conversion fails
              processedAttachments.push({
                filename: file.name,
                file_size: file.size,
                file_type: file.type,
                url: URL.createObjectURL(file)
              });
            }
          }
        }

        // Send via Pure Erlang WebSocket (ultra-fast, no Django calls)
        erlangWebSocketService.send({
          type: 'send_message',
          content,
          message_type: messageType,
          attachments: processedAttachments
        });
        
        // Add message to local state immediately with "sending" status
        const tempMessage: ChatMessage = {
          id: `temp-${Date.now()}`, // Temporary ID
          sender: {
            id: currentUser.id,
            name: currentUser.get_full_name || `${currentUser.first_name} ${currentUser.last_name}`,
            username: currentUser.username || currentUser.email,
            avatar: undefined // User type doesn't have avatar
          },
          content,
          message_type: messageType,
          status: 'sending', // Message being sent
          created_at: new Date().toISOString(),
          attachments: attachments?.map(file => ({
            id: `temp-attach-${Date.now()}`,
            filename: file.name,
            file_size: file.size,
            file_type: file.type,
            url: URL.createObjectURL(file)
          })) || []
        };
        
        setChatData(prev => {
          if (!prev) return null;
          return {
            ...prev,
            messages: [...prev.messages, tempMessage]
          };
        });
        
        // No Django persistence needed - WebSocket handles everything!
        
      } else {
        // Send via Django WebSocket (fallback)
        websocketService.sendMessage(content, messageType, replyingTo?.id);
      }

      // Clear message and reply
        setNewMessage('');
        setReplyingTo(null);
        
        // Clear file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

      // Stop typing indicator
      if (useErlangWebSocket) {
        erlangWebSocketService.sendTypingStop(currentUser.id);
      } else {
        websocketService.sendTypingStop();
      }
      setIsTyping(false);

    } catch (error: any) {
      console.error('Error sending message:', error);
      notificationService.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  // Handle message input
  const handleSendMessage = () => {
    if (newMessage.trim()) {
      sendMessage(newMessage.trim());
    }
  };

  // Handle typing indicators - Erlang-first with Django fallback
  const handleTyping = useCallback(() => {
    if (!isTyping) {
      setIsTyping(true);
      
      if (useErlangWebSocket && currentUser) {
        erlangWebSocketService.sendTypingStart(currentUser.id);
      } else {
        websocketService.sendTypingStart();
      }
    }

    // Clear existing timeout immediately
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    // Set new timeout to stop typing (faster for Erlang)
    const timeout = setTimeout(() => {
      if (useErlangWebSocket && currentUser) {
        erlangWebSocketService.sendTypingStop(currentUser.id);
      } else {
        websocketService.sendTypingStop();
      }
      setIsTyping(false);
      setTypingTimeout(null);
    }, useErlangWebSocket ? 300 : 500); // Faster for Erlang

    setTypingTimeout(timeout);
  }, [isTyping, typingTimeout, useErlangWebSocket, currentUser]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewMessage(e.target.value);

    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';

    // Handle typing indicator - instant response
    if (e.target.value.trim()) {
      handleTyping();
    } else {
      // Immediate stop when input is empty
      if (useErlangWebSocket && currentUser) {
        erlangWebSocketService.sendTypingStop(currentUser.id);
      } else {
        websocketService.sendTypingStop();
      }
      setIsTyping(false);
      if (typingTimeout) {
        clearTimeout(typingTimeout);
        setTypingTimeout(null);
      }
    }
  };

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      const content = `📎 ${file.name}`;
      sendMessage(content, 'file', [file]);
    }
  };

  // Handle key press
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  // Mark message as read - Pure WebSocket (no Django API calls)
  const markMessageAsRead = useCallback(async (messageId: string) => {
    try {
      // Check if message is already marked as read to prevent duplicate calls
      const message = chatData?.messages.find(msg => msg.id === messageId);
      if (message && message.status === 'read') {
        return; // Already read, no need to send again
      }

      if (useErlangWebSocket) {
        // Send read status via Pure Erlang WebSocket (ultra-fast)
        erlangWebSocketService.send({
          type: 'message_read',
          message_id: messageId
        });
        
        // No Django persistence needed - WebSocket handles everything!
      } else {
        // Send read status via Django WebSocket (fallback)
        websocketService.sendMessageRead(messageId);
      }
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  }, [useErlangWebSocket, chatData?.messages]);

  // Format time
  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });
    }
  };

  // Get user initials
  const getUserInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Check if message is from current user
  const isCurrentUser = (senderId: string) => {
    return currentUser && senderId === currentUser.id;
  };

  // Helper function to determine if message should show avatar and name
  const shouldShowAvatarAndName = (message: ChatMessage, index: number) => {
    if (isCurrentUser(message.sender.id)) return false; // Never show for current user

    // If this is the first message, show avatar and name
    if (index === 0) return true;

    // If previous message is from different user, show avatar and name
    const previousMessage = chatData?.messages[index - 1];
    if (previousMessage && previousMessage.sender.id !== message.sender.id) return true;

    return false;
  };

  // Helper function to determine if message should show avatar only (last message from user)
  const shouldShowAvatarOnly = (message: ChatMessage, index: number) => {
    if (isCurrentUser(message.sender.id)) return false; // Never show for current user

    // If this is the last message from this user, show avatar
    const nextMessage = chatData?.messages[index + 1];
    if (!nextMessage || nextMessage.sender.id !== message.sender.id) return true;

    return false;
  };


  if (loading) {
    return (
      <div className={styles.chatContainer}>
        {/* Messages Area Skeleton */}
        <div className={styles.messagesArea}>
          <MessageSkeleton count={5} />
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div className={styles.chatContainer}>
        <div className={styles.errorState}>
          <h3>Authentication required</h3>
          <p>Please log in to access the chat.</p>
        </div>
      </div>
    );
  }

  if (!chatData) {
    return (
      <div className={styles.chatContainer}>
        <div className={styles.errorState}>
          <h3>Chat not available</h3>
          <p>Unable to load chat for this task. Please check if you have permission to access this task.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.chatContainer}>
      
      {/* Connection Status Indicator */}

      {/* Messages Area */}
      <div className={styles.messagesArea}>
        {chatData.messages.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                <path d="M8 9h8" />
                <path d="M8 13h6" />
              </svg>
            </div>
            <h3>No messages yet</h3>
            <p>Start the conversation by sending a message!</p>
          </div>
        ) : (
          <div className={styles.messagesList} onScroll={handleScroll}>
            {chatData.messages.map((message, index) => {
              const isCurrentUserMessage = isCurrentUser(message.sender.id);
              const showAvatarAndName = shouldShowAvatarAndName(message, index);
              const showAvatarOnly = shouldShowAvatarOnly(message, index);
              
              return (
                <div 
                  key={`${message.id}-${message.created_at}`}
                  className={`${styles.messageWrapper} ${isCurrentUserMessage ? styles.messageWrapperRight : styles.messageWrapperLeft}`}
                  data-message-id={message.id}
                  data-sender-id={message.sender.id}
                >
                  <div className={styles.messageRow}>
                    {/* Avatar on the left (only for last message from other users) */}
                    {!isCurrentUserMessage && showAvatarOnly && (
                      <div className={styles.senderAvatar}>
                        {message.sender.avatar ? (
                          <img src={message.sender.avatar} alt={message.sender.name} />
                        ) : (
                          <span className={styles.avatarInitials}>{getUserInitials(message.sender.name)}</span>
                        )}
                    </div>
                  )}

                    {/* Spacer div to maintain consistent spacing when avatar is not present */}
                    {!isCurrentUserMessage && !showAvatarOnly && (
                      <div className={styles.avatarSpacer}></div>
                    )}

                      {/* Message content area */}
                      <div className={styles.messageContentArea}>
                  {/* Message bubble */}
                  <div className={`${styles.messageBubble} ${isCurrentUserMessage ? styles.messageBubbleRight : styles.messageBubbleLeft}`}>
                          {/* Sender name inside message bubble (only for first message from user) */}
                          {!isCurrentUserMessage && showAvatarAndName && (
                            <div className={styles.senderName}>{message.sender.name}</div>
                          )}
                    {/* Reply indicator */}
                    {message.reply_to && (
                      <div className={styles.replyIndicator}>
                        <div className={styles.replyLine}></div>
                        <div className={styles.replyContent}>
                          <span className={styles.replySender}>{message.reply_to.sender}</span>
                          <span className={styles.replyText}>{message.reply_to.content}</span>
                        </div>
                      </div>
                    )}

                    {/* Forwarded indicator */}
                    {message.forwarded_from && (
                      <div className={styles.forwardedIndicator}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                              <polyline points="16,6 12,2 8,6" />
                              <line x1="12" y1="2" x2="12" y2="15" />
                        </svg>
                        <span>Forwarded from {message.forwarded_from.sender}</span>
                      </div>
                    )}

                    {/* Message content */}
                    <div className={styles.messageContent}>
                      {message.message_type === 'text' && (
                        <p className={styles.messageText}>{message.content}</p>
                      )}
                      
                      {message.message_type === 'file' && (
                        <div className={styles.fileMessage}>
                              {message.attachments && message.attachments.length > 0 && (() => {
                                const attachment = message.attachments[0];
                                const fileExtension = attachment.filename.split('.').pop()?.toLowerCase();
                                
                                return (
                                  <div
                                    className={styles.fileAttachment}
                                    onClick={() => {
                                // For PDFs and images, open in new tab for viewing
                                if (fileExtension === 'pdf' || ['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension || '')) {
                                  handleFileAction(attachment.url, attachment.filename, 'view');
                                } else {
                                  // For other files, trigger download
                                  handleFileAction(attachment.url, attachment.filename, 'download');
                                      }
                                    }}
                                  >
                                    <div className={`${styles.fileIcon} ${
                                      fileExtension === 'pdf' ? styles.pdf :
                                      ['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension || '') ? styles.img :
                                      ['doc', 'docx'].includes(fileExtension || '') ? styles.doc :
                                      ['xls', 'xlsx'].includes(fileExtension || '') ? styles.xls :
                                      ['zip', 'rar', '7z'].includes(fileExtension || '') ? styles.zip :
                                      styles.pdf
                                    }`}>
                                      {fileExtension?.toUpperCase() || 'FILE'}
                            </div>
                            <div className={styles.fileInfo}>
                              <span className={styles.fileName}>
                                        {attachment.filename}
                              </span>
                              <div className={styles.fileDetails}>
                                <span>
                                          {attachment.page_count ? `${attachment.page_count} pages` : 
                                           ['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension || '') ? 'Image file' :
                                           'Document'}
                                </span>
                                <span>•</span>
                                <span className={styles.fileSize}>
                                          {attachment.file_size ? `${(attachment.file_size / 1024).toFixed(0)} KB` : 'Unknown size'}
                                </span>
                                <span>•</span>
                                <span>
                                          {fileExtension || 'file'}
                                </span>
                              </div>
                            </div>
                            <div className={styles.fileActions}>
                              <button 
                                className={styles.viewBtn}
                                onClick={(e) => {
                                  e.stopPropagation();
                                    handleFileAction(attachment.url, attachment.filename, 'view');
                                }}
                                title="View file"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                          <circle cx="12" cy="12" r="3" />
                                </svg>
                              </button>
                              <button 
                                className={styles.downloadBtn}
                                onClick={(e) => {
                                  e.stopPropagation();
                                    handleFileAction(attachment.url, attachment.filename, 'download');
                                }}
                                title="Download file"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                          <polyline points="7,10 12,15 17,10" />
                                          <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                              </button>
                            </div>
                          </div>
                                );
                              })()}
                        </div>
                      )}
                      
                      {/* Attachments */}
                      {message.attachments && message.attachments.length > 0 && message.message_type !== 'file' && (
                        <div className={styles.attachments}>
                          {message.attachments.map((attachment) => {
                            const fileExtension = attachment.filename.split('.').pop()?.toLowerCase();
                            const iconClass = fileExtension === 'pdf' ? styles.pdf : 
                                            fileExtension === 'doc' || fileExtension === 'docx' ? styles.doc :
                                            fileExtension === 'xls' || fileExtension === 'xlsx' ? styles.xls :
                                            ['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension || '') ? styles.img :
                                            ['zip', 'rar', '7z'].includes(fileExtension || '') ? styles.zip : styles.pdf;
                            
                            return (
                              <div key={attachment.id} className={styles.attachment}>
                                <div 
                                  className={styles.attachmentContent}
                                  onClick={() => {
                                    // For PDFs and images, open in new tab for viewing
                                    if (fileExtension === 'pdf' || ['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension || '')) {
                                      handleFileAction(attachment.url, attachment.filename, 'view');
                                    } else {
                                      // For other files, trigger download
                                      handleFileAction(attachment.url, attachment.filename, 'download');
                                    }
                                  }}
                                >
                                  <div className={`${styles.fileIcon} ${iconClass}`}>
                                    {fileExtension?.toUpperCase() || 'FILE'}
                                  </div>
                                  <div className={styles.fileInfo}>
                                    <span className={styles.fileName}>{attachment.filename}</span>
                                    <div className={styles.fileDetails}>
                                      <span className={styles.fileSize}>
                                        {(attachment.file_size / 1024).toFixed(0)} KB
                                      </span>
                                      <span>•</span>
                                      <span>{fileExtension || 'file'}</span>
                                    </div>
                                  </div>
                                  <div className={styles.fileActions}>
                                    <button 
                                      className={styles.viewBtn}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleFileAction(attachment.url, attachment.filename, 'view');
                                      }}
                                      title="View file"
                                    >
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                            <circle cx="12" cy="12" r="3" />
                                      </svg>
                                    </button>
                                    <button 
                                      className={styles.downloadBtn}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleFileAction(attachment.url, attachment.filename, 'download');
                                      }}
                                      title="Download file"
                                    >
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                            <polyline points="7,10 12,15 17,10" />
                                            <line x1="12" y1="15" x2="12" y2="3" />
                                      </svg>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                        </div>

                        {/* Message metadata - moved to be a sibling of messageContent */}
                      <div className={styles.messageMeta}>
                        <span className={styles.messageTime}>{formatTime(message.created_at)}</span>
                        {isCurrentUserMessage && (
                          <div className={styles.messageStatus}>
                              {/* Single tick - Message sent (stored in backend) */}
                            {message.status === 'sent' && (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <polyline points="20,6 9,17 4,12" />
                              </svg>
                            )}
                              
                              {/* Double tick - Message delivered */}
                            {message.status === 'delivered' && (
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-check-all" viewBox="0 0 16 16">
                                  <path d="M8.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L2.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093L8.95 4.992zm-.92 5.14.92.92a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 1 0-1.091-1.028L9.477 9.417l-.485-.486z"/>
                              </svg>
                            )}
                              
                              {/* Double tick (blue) - Message read */}
                            {message.status === 'read' && (
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#4fc3f7" className="bi bi-check-all" viewBox="0 0 16 16">
                                  <path d="M8.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L2.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093L8.95 4.992zm-.92 5.14.92.92a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 1 0-1.091-1.028L9.477 9.417l-.485-.486z"/>
                                </svg>
                              )}
                              
                              {/* Failed message */}
                              {message.status === 'failed' && (
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f44336" strokeWidth="2">
                                  <circle cx="12" cy="12" r="10" />
                                  <line x1="15" y1="9" x2="9" y2="15" />
                                  <line x1="9" y1="9" x2="15" y2="15" />
                              </svg>
                            )}
                          </div>
                        )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* Typing Indicator - positioned below last message, always at bottom */}
            {typingUsersList.length > 0 && (
              <div className={styles.typingIndicator}>
               
                {typingUsersList.map((user, index) => (
                  <div key={user.id} className={styles.typingUser}>
                    {/* Avatar */}
                    <div className={styles.typingAvatar}>
                      {user.avatar ? (
                        <img src={user.avatar} alt={user.name} />
                      ) : (
                        <span className={styles.avatarInitials}>{getUserInitials(user.name)}</span>
                      )}
                    </div>
                    
                    {/* Typing bubble with name */}
                    <div className={styles.typingBubble}>
                      <div className={styles.typingName}>{user.name} is typing</div>
                      <div className={styles.typingAnimation}>
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Load More Button */}
            {pagination.has_next && (
              <div className={styles.loadMoreContainer}>
                <button 
                  className={styles.loadMoreButton}
                  onClick={loadMoreMessages}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? (
                    <>
                      <div className={styles.spinner}></div>
                      Loading more messages...
                    </>
                  ) : (
                    `Load more messages (${pagination.total_messages - (pagination.current_page * pagination.page_size)} remaining)`
                  )}
                </button>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* WhatsApp-style Message Input */}
      <div className={styles.messageInput}>
        {/* Reply indicator */}
        {replyingTo && (
          <div className={styles.replyPreview}>
            <div className={styles.replyPreviewContent}>
              <span className={styles.replyPreviewSender}>Replying to {replyingTo?.sender?.name}</span>
              <span className={styles.replyPreviewText}>{replyingTo?.content}</span>
            </div>
            <button 
              className={styles.replyCancel}
              onClick={() => setReplyingTo(null)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        )}


        <div className={styles.inputContainer}>
          <button 
            className={styles.attachBtn}
            onClick={() => fileInputRef.current?.click()}
            title="Attach file"
          >
            <span className={styles.plusIcon}>+</span>
          </button>

          <div className={styles.textInputContainer}>
            <textarea
              ref={messageInputRef}
              value={newMessage}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              className={styles.textInput}
              rows={1}
            />
          </div>

          <button 
            className={styles.sendBtn}
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || sendingMessage}
            title="Send message"
          >
            {sendingMessage ? (
              <div className={styles.sendingSpinner}></div>
            ) : (
              <svg width="16" height="16" fill="currentColor" className={styles.sendIcon} viewBox="0 0 16 16">
                <path d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576zm6.787-8.201L1.591 6.602l4.339 2.76z" />
              </svg>
            )}
          </button>
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileUpload}
          style={{ display: 'none' }}
          accept="image/*,application/pdf,.doc,.docx,.txt,.zip"
        />
      </div>
    </div>
  );
};

export default Chat;